import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { X, GripVertical, Save, ArrowLeft, Loader2, Users, Layers, Trash2, ChevronUp, ChevronDown } from 'lucide-react';
import { useSupabase } from '../contexts/SupabaseContext';
import { useBusiness } from '../contexts/BusinessContext';
import { toast } from 'sonner';

interface QueueEntry {
  id: string;
  booking_id: string;
  queue_position: number;
  booking: {
    id: string;
    customer?: {
      first_name: string;
      last_name: string;
    };
    services?: Array<{
      name: string;
      vehicle?: {
        make: string;
        model: string;
        year: string;
        color: string;
      };
    }>;
  };
}

interface StageEntry {
  id: string;
  booking_id: string;
  stage_id: string;
  status: 'active' | 'completed';
  position?: number;
  booking: {
    id: string;
    customer?: {
      first_name: string;
      last_name: string;
    };
    services?: Array<{
      name: string;
      vehicle?: {
        make: string;
        model: string;
        year: string;
        color: string;
      };
    }>;
  };
}

interface Stage {
  id: string;
  title: string;
  stage_order: number;
}

interface DraggableItem {
  id: string;
  bookingId: string;
  entryId: string;
  type: 'queue' | 'stage';
  stageId?: string;
  status?: 'active' | 'completed';
  customerName: string;
  serviceName: string;
  vehicleInfo: string;
}

interface QueueReorderModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
}

export default function QueueReorderModal({ isOpen, onClose, onSave }: QueueReorderModalProps) {
  const supabase = useSupabase();
  const { selectedBusiness } = useBusiness();
  
  const [isAnimating, setIsAnimating] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [stages, setStages] = useState<Stage[]>([]);
  
  // State for drag and drop
  const [queueItems, setQueueItems] = useState<DraggableItem[]>([]);
  const [stageItems, setStageItems] = useState<{ [stageId: string]: DraggableItem[] }>({});
  
  // Drag state
  const [draggedItem, setDraggedItem] = useState<DraggableItem | null>(null);
  const [dragOverTarget, setDragOverTarget] = useState<string | null>(null);
  
  // Track deleted bookings
  const [deletedBookings, setDeletedBookings] = useState<DraggableItem[]>([]);

  // Load data when modal opens
  const loadData = useCallback(async () => {
    if (!selectedBusiness?.id) return;
    
    setIsLoading(true);
    try {
      // Fetch stages
      const { data: stagesData, error: stagesError } = await supabase
        .from('queue_handoff_stages')
        .select('id, title, stage_order')
        .eq('business_id', selectedBusiness.id)
        .order('stage_order', { ascending: true });

      if (stagesError) throw stagesError;
      setStages(stagesData || []);

      // Fetch queue entries
      const { data: queueData, error: queueError } = await supabase
        .from('queue_entries')
        .select(`
          id,
          booking_id,
          queue_position,
          booking:booking_id(
            id,
            customer:customers!bookings_customer_id_fkey(first_name, last_name),
            services:booking_services(
              name,
              vehicle:customer_vehicles(make, model, year, color)
            )
          )
        `)
        .eq('business_id', selectedBusiness.id)
        .eq('status', 'waiting')
        .order('queue_position', { ascending: true });

      if (queueError) throw queueError;

      // Transform queue data
      const queueItemsData: DraggableItem[] = (queueData || []).map((entry: any) => ({
        id: `queue-${entry.booking_id}`,
        bookingId: entry.booking_id,
        entryId: entry.id,
        type: 'queue' as const,
        customerName: entry.booking?.customer 
          ? `${entry.booking.customer.first_name || ''} ${entry.booking.customer.last_name || ''}`.trim()
          : 'Unknown',
        serviceName: entry.booking?.services?.[0]?.name || 'No service',
        vehicleInfo: entry.booking?.services?.[0]?.vehicle
          ? `${entry.booking.services[0].vehicle.year} ${entry.booking.services[0].vehicle.make} ${entry.booking.services[0].vehicle.model}`
          : 'No vehicle'
      }));
      setQueueItems(queueItemsData);

      // Fetch stage entries
      const { data: stageEntriesData, error: stageEntriesError } = await supabase
        .from('queue_handoff_entries')
        .select(`
          id,
          booking_id,
          stage_id,
          status,
          position,
          entered_at,
          booking:booking_id(
            id,
            customer:customers!bookings_customer_id_fkey(first_name, last_name),
            services:booking_services(
              name,
              vehicle:customer_vehicles(make, model, year, color)
            )
          )
        `)
        .eq('business_id', selectedBusiness.id)
        .in('status', ['active', 'completed'])
        .order('position', { ascending: true })
        .order('entered_at', { ascending: true });

      if (stageEntriesError) throw stageEntriesError;

      // Group by stage
      const stageItemsMap: { [stageId: string]: DraggableItem[] } = {};
      (stagesData || []).forEach(stage => {
        stageItemsMap[stage.id] = [];
      });

      (stageEntriesData || []).forEach((entry: any) => {
        if (stageItemsMap[entry.stage_id]) {
          stageItemsMap[entry.stage_id].push({
            id: `stage-${entry.booking_id}`,
            bookingId: entry.booking_id,
            entryId: entry.id,
            type: 'stage' as const,
            stageId: entry.stage_id,
            status: entry.status,
            customerName: entry.booking?.customer 
              ? `${entry.booking.customer.first_name || ''} ${entry.booking.customer.last_name || ''}`.trim()
              : 'Unknown',
            serviceName: entry.booking?.services?.[0]?.name || 'No service',
            vehicleInfo: entry.booking?.services?.[0]?.vehicle
              ? `${entry.booking.services[0].vehicle.year} ${entry.booking.services[0].vehicle.make} ${entry.booking.services[0].vehicle.model}`
              : 'No vehicle'
          });
        }
      });
      setStageItems(stageItemsMap);

    } catch (error) {
      console.error('Error loading queue data:', error);
      toast.error('Failed to load queue data');
    } finally {
      setIsLoading(false);
    }
  }, [selectedBusiness?.id, supabase]);

  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
      document.body.style.overflow = 'hidden';
      setDeletedBookings([]); // Reset deleted bookings when modal opens
      loadData();
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, loadData]);

  const handleTransitionEnd = () => {
    if (!isOpen) {
      setIsAnimating(false);
    }
  };

  // Drag handlers
  const handleDragStart = (e: React.DragEvent, item: DraggableItem) => {
    setDraggedItem(item);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', item.id);
    // Add a slight delay to show the drag preview
    setTimeout(() => {
      (e.target as HTMLElement).style.opacity = '0.5';
    }, 0);
  };

  const handleDragEnd = (e: React.DragEvent) => {
    (e.target as HTMLElement).style.opacity = '1';
    setDraggedItem(null);
    setDragOverTarget(null);
  };

  const handleDragOver = (e: React.DragEvent, targetId: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverTarget(targetId);
  };

  const handleDragLeave = () => {
    setDragOverTarget(null);
  };

  const handleDropOnQueue = (e: React.DragEvent) => {
    e.preventDefault();
    if (!draggedItem) return;

    // Only allow dropping from stages, not reordering within queue
    if (draggedItem.type === 'queue') {
      setDragOverTarget(null);
      return;
    }

    console.log('📥 Moving from stage to queue:', {
      bookingId: draggedItem.bookingId,
      fromStage: draggedItem.stageId
    });

    // Remove from stage
    setStageItems(prev => {
      const newStageItems = { ...prev };
      Object.keys(newStageItems).forEach(stageId => {
        newStageItems[stageId] = newStageItems[stageId].filter(item => item.id !== draggedItem.id);
      });
      return newStageItems;
    });

    // Add to END of queue
    const newItem: DraggableItem = {
      ...draggedItem,
      id: `queue-${draggedItem.bookingId}`,
      type: 'queue',
      stageId: undefined,
      status: undefined
    };

    setQueueItems(prev => [...prev, newItem]);
    setDragOverTarget(null);
  };

  // Move queue item up
  const handleMoveUp = (index: number) => {
    if (index <= 0) return;
    setQueueItems(prev => {
      const newItems = [...prev];
      [newItems[index - 1], newItems[index]] = [newItems[index], newItems[index - 1]];
      return newItems;
    });
  };

  // Move queue item down
  const handleMoveDown = (index: number) => {
    setQueueItems(prev => {
      if (index >= prev.length - 1) return prev;
      const newItems = [...prev];
      [newItems[index], newItems[index + 1]] = [newItems[index + 1], newItems[index]];
      return newItems;
    });
  };

  const handleDropOnStage = (e: React.DragEvent, stageId: string, dropIndex?: number) => {
    e.preventDefault();
    if (!draggedItem) return;

    // Remove from current location
    if (draggedItem.type === 'queue') {
      setQueueItems(prev => prev.filter(item => item.id !== draggedItem.id));
    } else {
      setStageItems(prev => {
        const newStageItems = { ...prev };
        Object.keys(newStageItems).forEach(sid => {
          newStageItems[sid] = newStageItems[sid].filter(item => item.id !== draggedItem.id);
        });
        return newStageItems;
      });
    }

    // Add to stage at specified position
    const newItem: DraggableItem = {
      ...draggedItem,
      id: `stage-${draggedItem.bookingId}`,
      type: 'stage',
      stageId: stageId,
      status: draggedItem.status || 'active'
    };

    setStageItems(prev => {
      const newStageItems = { ...prev };
      const stageItemsList = [...(newStageItems[stageId] || [])];
      const insertIndex = dropIndex !== undefined ? dropIndex : stageItemsList.length;
      stageItemsList.splice(insertIndex, 0, newItem);
      newStageItems[stageId] = stageItemsList;
      return newStageItems;
    });

    setDragOverTarget(null);
  };

  // Handle deleting a booking from queue or stage
  const handleDeleteBooking = (item: DraggableItem) => {
    console.log('🗑️ Deleting booking:', item);
    
    // Remove from queue or stage
    if (item.type === 'queue') {
      setQueueItems(prev => prev.filter(i => i.id !== item.id));
    } else {
      setStageItems(prev => {
        const newStageItems = { ...prev };
        Object.keys(newStageItems).forEach(stageId => {
          newStageItems[stageId] = newStageItems[stageId].filter(i => i.id !== item.id);
        });
        return newStageItems;
      });
    }
    
    // Add to deleted list
    setDeletedBookings(prev => [...prev, item]);
  };

  // Undo delete
  const handleUndoDelete = (item: DraggableItem) => {
    console.log('↩️ Undoing delete:', item);
    
    // Remove from deleted list
    setDeletedBookings(prev => prev.filter(i => i.bookingId !== item.bookingId));
    
    // Add back to original location
    if (item.type === 'queue' || !item.stageId) {
      setQueueItems(prev => [...prev, { ...item, id: `queue-${item.bookingId}`, type: 'queue' }]);
    } else {
      setStageItems(prev => {
        const newStageItems = { ...prev };
        if (item.stageId && newStageItems[item.stageId]) {
          newStageItems[item.stageId] = [...newStageItems[item.stageId], item];
        }
        return newStageItems;
      });
    }
  };

  // Save changes
  const handleSave = async () => {
    if (!selectedBusiness?.id) return;

    setIsSaving(true);
    try {
      // Build queue order array
      const queueOrder = queueItems.map((item, index) => ({
        booking_id: item.bookingId,
        position: index + 1
      }));

      // Build stage assignments array
      const stageAssignments: any[] = [];
      Object.entries(stageItems).forEach(([stageId, items]) => {
        items.forEach((item, index) => {
          stageAssignments.push({
            booking_id: item.bookingId,
            stage_id: stageId,
            position: index + 1,
            entry_status: item.status || 'active'
          });
        });
      });

      // Build deleted bookings array
      const deletedBookingIds = deletedBookings.map(item => item.bookingId);

      console.log('🔄 Saving reorder...');
      console.log('📋 Queue order:', JSON.stringify(queueOrder, null, 2));
      console.log('📋 Stage assignments:', JSON.stringify(stageAssignments, null, 2));
      console.log('🗑️ Deleted bookings:', deletedBookingIds);
      console.log('🏢 Business ID:', selectedBusiness.id);

      // Process deletions first
      for (const bookingId of deletedBookingIds) {
        console.log(`🗑️ Processing deletion for booking ${bookingId}`);
        
        // Mark queue entry as removed
        const { error: queueError } = await supabase
          .from('queue_entries')
          .update({ status: 'removed', updated_at: new Date().toISOString() })
          .eq('booking_id', bookingId)
          .eq('business_id', selectedBusiness.id);
        
        if (queueError) console.error('Error removing queue entry:', queueError);
        
        // Mark stage entry as removed
        const { error: stageError } = await supabase
          .from('queue_handoff_entries')
          .update({ status: 'removed', updated_at: new Date().toISOString() })
          .eq('booking_id', bookingId)
          .eq('business_id', selectedBusiness.id);
        
        if (stageError) console.error('Error removing stage entry:', stageError);
        
        // Reset booking status
        const { error: bookingError } = await supabase
          .from('bookings')
          .update({ 
            in_que: false, 
            status: 'scheduled',
            start_date: null,
            start_time: null,
            updated_at: new Date().toISOString() 
          })
          .eq('id', bookingId);
        
        if (bookingError) console.error('Error resetting booking:', bookingError);
      }

      // Call the RPC function
      const { data, error } = await supabase.rpc('reorder_queue_and_stages', {
        p_business_id: selectedBusiness.id,
        p_queue_order: queueOrder,
        p_stage_assignments: stageAssignments
      });

      console.log('📦 RPC Response:', { data, error });

      if (error) {
        console.error('❌ RPC Error:', error);
        throw error;
      }

      if (data && !data.success) {
        console.error('❌ RPC returned failure:', data);
        throw new Error(data.error || 'Failed to save changes');
      }

      console.log('✅ Reorder successful:', data);
      
      const totalChanges = (data?.changes?.length || 0) + deletedBookingIds.length;
      toast.success(`Queue updated! ${totalChanges} change${totalChanges !== 1 ? 's' : ''} saved.`);
      onSave();
      onClose();
    } catch (error) {
      console.error('Error saving reorder:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to save changes');
    } finally {
      setIsSaving(false);
    }
  };

  // Render a queue item card (with up/down arrows for reordering)
  const renderQueueItemCard = (item: DraggableItem, index: number, totalItems: number) => (
    <div
      key={item.id}
      className="flex items-center gap-2 p-3 bg-white border rounded-lg transition-all hover:shadow-md hover:border-blue-300"
    >
      {/* Up/Down arrows for reordering */}
      <div className="flex flex-col gap-0.5 flex-shrink-0">
        <button
          onClick={() => handleMoveUp(index)}
          disabled={index === 0}
          className={`p-0.5 rounded transition-colors ${
            index === 0 
              ? 'text-gray-200 cursor-not-allowed' 
              : 'text-gray-400 hover:text-blue-600 hover:bg-blue-50'
          }`}
          title="Move up"
        >
          <ChevronUp className="h-4 w-4" />
        </button>
        <button
          onClick={() => handleMoveDown(index)}
          disabled={index === totalItems - 1}
          className={`p-0.5 rounded transition-colors ${
            index === totalItems - 1 
              ? 'text-gray-200 cursor-not-allowed' 
              : 'text-gray-400 hover:text-blue-600 hover:bg-blue-50'
          }`}
          title="Move down"
        >
          <ChevronDown className="h-4 w-4" />
        </button>
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-medium text-sm text-gray-900 truncate">
          {item.customerName}
        </div>
        <div className="text-xs text-gray-500 truncate">
          {item.serviceName}
        </div>
        <div className="text-xs text-gray-400 truncate">
          {item.vehicleInfo}
        </div>
      </div>
      <span className="text-xs font-medium text-blue-600 bg-blue-50 px-2 py-0.5 rounded flex-shrink-0">
        #{index + 1}
      </span>
      <button
        onClick={(e) => {
          e.stopPropagation();
          handleDeleteBooking(item);
        }}
        className="p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors flex-shrink-0"
        title="Remove from queue"
      >
        <Trash2 className="h-4 w-4" />
      </button>
    </div>
  );

  // Render a stage item card (draggable)
  const renderStageItemCard = (item: DraggableItem, index: number) => (
    <div
      key={item.id}
      draggable
      onDragStart={(e) => handleDragStart(e, item)}
      onDragEnd={handleDragEnd}
      className={`flex items-center gap-2 p-3 bg-white border rounded-lg cursor-grab active:cursor-grabbing transition-all ${
        draggedItem?.id === item.id ? 'opacity-50 scale-95' : 'hover:shadow-md hover:border-blue-300'
      } ${item.status === 'completed' ? 'border-l-4 border-l-green-500' : ''}`}
    >
      <GripVertical className="h-4 w-4 text-gray-400 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="font-medium text-sm text-gray-900 truncate">
          {item.customerName}
        </div>
        <div className="text-xs text-gray-500 truncate">
          {item.serviceName}
        </div>
        <div className="text-xs text-gray-400 truncate">
          {item.vehicleInfo}
        </div>
      </div>
      {item.status === 'completed' && (
        <span className="text-[10px] font-medium text-green-600 bg-green-50 px-1.5 py-0.5 rounded">
          Done
        </span>
      )}
      <span className="text-xs text-gray-400 flex-shrink-0">
        #{index + 1}
      </span>
      <button
        onClick={(e) => {
          e.stopPropagation();
          handleDeleteBooking(item);
        }}
        className="p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors flex-shrink-0"
        title="Remove from stage"
      >
        <Trash2 className="h-4 w-4" />
      </button>
    </div>
  );

  // Render a deleted item card (for undo)
  const renderDeletedCard = (item: DraggableItem) => (
    <div
      key={`deleted-${item.bookingId}`}
      className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg"
    >
      <div className="flex-1 min-w-0">
        <div className="font-medium text-sm text-gray-900 truncate">
          {item.customerName}
        </div>
        <div className="text-xs text-gray-500 truncate">
          {item.serviceName}
        </div>
      </div>
      <span className="text-[10px] font-medium text-red-600 bg-red-100 px-1.5 py-0.5 rounded">
        Removed
      </span>
      <button
        onClick={() => handleUndoDelete(item)}
        className="text-xs font-medium text-blue-600 hover:text-blue-700 px-2 py-1 rounded hover:bg-blue-50 transition-colors"
      >
        Undo
      </button>
    </div>
  );

  if (!isOpen && !isAnimating) return null;

  const modalContent = (
    <div
      className={`fixed inset-0 z-[100000] transition-opacity duration-200 ${
        isOpen ? 'bg-black/50' : 'bg-black/0'
      }`}
      onClick={onClose}
      onTransitionEnd={handleTransitionEnd}
    >
      <div
        className={`fixed inset-0 bg-white transform transition-transform duration-300 ${
          isOpen ? 'translate-y-0' : 'translate-y-full'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 z-10 bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <ArrowLeft className="h-5 w-5 text-gray-600" />
            </button>
            <h2 className="text-lg font-semibold text-gray-900">Reorder Queue & Stages</h2>
          </div>
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
          >
            {isSaving ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="h-4 w-4" />
                Save Changes
              </>
            )}
          </button>
        </div>

        {/* Content */}
        <div className="h-[calc(100vh-64px)] overflow-auto p-4">
          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
            </div>
          ) : (
            <>
              {/* Deleted Items Section */}
              {deletedBookings.length > 0 && (
                <div className="max-w-7xl mx-auto mb-4">
                  <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <Trash2 className="h-5 w-5 text-red-600" />
                      <h3 className="font-semibold text-red-900">
                        Bookings to Remove ({deletedBookings.length})
                      </h3>
                      <span className="text-xs text-red-600 ml-2">
                        These will be removed from the queue system when you save
                      </span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                      {deletedBookings.map(item => renderDeletedCard(item))}
                    </div>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4 max-w-7xl mx-auto">
              {/* Queue Column */}
              <div
                className={`bg-gray-50 rounded-xl border-2 border-dashed transition-colors ${
                  dragOverTarget === 'queue' ? 'border-blue-400 bg-blue-50' : 'border-gray-200'
                }`}
                onDragOver={(e) => handleDragOver(e, 'queue')}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDropOnQueue(e)}
              >
                <div className="p-4 border-b border-gray-200 bg-white rounded-t-xl">
                  <div className="flex items-center gap-2">
                    <Users className="h-5 w-5 text-blue-600" />
                    <h3 className="font-semibold text-gray-900">Queue</h3>
                    <span className="ml-auto text-sm text-gray-500">
                      {queueItems.length} {queueItems.length === 1 ? 'booking' : 'bookings'}
                    </span>
                  </div>
                </div>
                <div className="p-3 space-y-2 min-h-[200px]">
                  {queueItems.length === 0 ? (
                    <div className="text-center text-gray-400 text-sm py-8">
                      Drag bookings here from stages
                    </div>
                  ) : (
                    queueItems.map((item, index) => renderQueueItemCard(item, index, queueItems.length))
                  )}
                </div>
              </div>

              {/* Stage Columns */}
              {stages.map((stage) => (
                <div
                  key={stage.id}
                  className={`bg-gray-50 rounded-xl border-2 border-dashed transition-colors ${
                    dragOverTarget === stage.id ? 'border-blue-400 bg-blue-50' : 'border-gray-200'
                  }`}
                  onDragOver={(e) => handleDragOver(e, stage.id)}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDropOnStage(e, stage.id)}
                >
                  <div className="p-4 border-b border-gray-200 bg-white rounded-t-xl">
                    <div className="flex items-center gap-2">
                      <Layers className="h-5 w-5 text-amber-600" />
                      <h3 className="font-semibold text-gray-900">
                        Stage {stage.stage_order}: {stage.title}
                      </h3>
                      <span className="ml-auto text-sm text-gray-500">
                        {(stageItems[stage.id] || []).length} {(stageItems[stage.id] || []).length === 1 ? 'booking' : 'bookings'}
                      </span>
                    </div>
                  </div>
                  <div className="p-3 space-y-2 min-h-[200px]">
                    {(stageItems[stage.id] || []).length === 0 ? (
                      <div className="text-center text-gray-400 text-sm py-8">
                        Drag bookings here
                      </div>
                    ) : (
                      (stageItems[stage.id] || []).map((item, index) => renderStageItemCard(item, index))
                    )}
                  </div>
                </div>
              ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}

